/**
 * NWZ Lightbox - Barrierefreie Lightbox-Lösung
 * WCAG 2.1 AA konform
 * Keine externen Abhängigkeiten
 * 
 * Features:
 * - Keyboard Navigation (Enter, Space, ESC, Arrow Keys)
 * - Focus Management (Focus Trap)
 * - Screen Reader Support
 * - Touch Swipe Support
 * - Responsive
 */

interface LightboxImage {
  index: number;
  url: string;
  alt: string;
  caption: string;
  gallery: string;
  trigger: HTMLAnchorElement;
}

interface LightboxOptions {
  loop?: boolean;
  keyboard?: boolean;
}

export class Lightbox {
  private selector: string;
  private options: Required<LightboxOptions>;
  private images: LightboxImage[] = [];
  private currentIndex: number = 0;
  private isOpen: boolean = false;
  private links: NodeListOf<HTMLAnchorElement>;
  private previousFocus: HTMLElement | null = null;
  
  private dialog: HTMLDialogElement | null = null;
  private content: HTMLDivElement | null = null;
  
  constructor(selector: string = 'a[data-lightbox]', options: LightboxOptions = {}) {
    this.selector = selector;
    this.links = document.querySelectorAll<HTMLAnchorElement>(this.selector);

    this.options = {
      loop: true,
      keyboard: true,
      ...options
    };
    
    this.init();
  }
  
  private init(): void {
    if (this.links.length === 0) {
      return;
    }

    this.prepareLinks();
    this.collectImages();
    this.createLightbox();
    this.bindEvents();
  }
  
  private prepareLinks(): void {
    this.links.forEach((link: HTMLAnchorElement) => {
      // Progressive Enhancement: Nur mit JS werden Links zu Buttons
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'dialog');
    });
  }
  
  private collectImages(): void {
    this.links.forEach((link: HTMLAnchorElement, index: number) => {
      const img = link.querySelector<HTMLImageElement>('img');
      const gallery = link.dataset.gallery || 'default';
      
      this.images.push({
        index: index,
        url: link.href,
        alt: img?.alt || '',
        caption: link.dataset.caption || img?.title || img?.alt || '',
        gallery: gallery,
        trigger: link
      });
    });
  }
  
  private createLightbox(): void {
    // Dialog Element (natives HTML5)
    this.dialog = document.createElement('dialog');
    this.dialog.className = 'nwz-lightbox-dialog';
    this.dialog.setAttribute('aria-label', 'Bildergalerie');
    
    // Content
    this.content = document.createElement('div');
    this.content.className = 'nwz-lightbox-content';
    this.content.innerHTML = `
      <button type="button" class="nwz-lightbox-close" aria-label="Schließen">
        <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
      <button type="button" class="nwz-lightbox-btn nwz-lightbox-btn-prev" aria-label="Vorheriges Bild">
        <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>
      <button type="button" class="nwz-lightbox-btn nwz-lightbox-btn-next" aria-label="Nächstes Bild">
        <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>
      <img class="nwz-lightbox-image" src="" alt="" />
      <div class="nwz-lightbox-caption"></div>
      <div class="visually-hidden" role="status" aria-live="polite" aria-atomic="true"></div>
    `;
    
    this.dialog.appendChild(this.content);
    document.body.appendChild(this.dialog);
  }
  
  private bindEvents(): void {
    if (!this.dialog) return;
    
    // Trigger Links
    this.images.forEach((image: LightboxImage, index: number) => {
      const trigger = image.trigger;
      
      // Click & Enter
      trigger.addEventListener('click', (e: MouseEvent) => {
        e.preventDefault();
        this.open(index);
      });
      
      // Space-Taste (wie bei Buttons)
      trigger.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          this.open(index);
        }
      });
    });
    
    // Close Button
    const closeBtn = this.dialog.querySelector<HTMLButtonElement>('.nwz-lightbox-close');
    closeBtn?.addEventListener('click', () => this.close());
    
    // Navigation Buttons
    const prevBtn = this.dialog.querySelector<HTMLButtonElement>('.nwz-lightbox-btn-prev');
    const nextBtn = this.dialog.querySelector<HTMLButtonElement>('.nwz-lightbox-btn-next');
    
    prevBtn?.addEventListener('click', () => this.prev());
    nextBtn?.addEventListener('click', () => this.next());
    
    // Backdrop Click (Klick außerhalb des Dialogs)
    this.dialog.addEventListener('click', (e: MouseEvent) => {
      // Nur schließen, wenn direkt auf das Dialog-Element geklickt wurde (nicht auf Kindelemente)
      if (e.target === this.dialog) {
        this.close();
      }
    });
    
    // Keyboard Navigation
    if (this.options.keyboard) {
      document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (!this.isOpen) return;
        
        switch(e.key) {
          case 'Escape':
          case 'Esc':
            e.preventDefault();
            this.close();
            break;
          case 'ArrowLeft':
            e.preventDefault();
            this.prev();
            break;
          case 'ArrowRight':
            e.preventDefault();
            this.next();
            break;
        }
      });
    }
    
    // Focus Trap
    this.dialog.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        this.trapFocus(e);
      }
    });
  }
  
  private open(index: number): void {
    if (!this.dialog) return;
    
    this.currentIndex = index;
    this.isOpen = true;
    
    // Speichere aktuelles Focus-Element (für Fallback)
    this.previousFocus = document.activeElement as HTMLElement;
    
    // Lade Bild
    this.loadImage(index);
    
    // Zeige Dialog modal (managed focus automatisch)
    this.dialog.showModal();
    
    // Screen Reader Announcement
    this.announce(
      `Lightbox geöffnet. Bild ${index + 1} von ${this.images.length}. ` +
      'Nutzen Sie die Pfeiltasten zum Navigieren, Escape zum Schließen.'
    );
  }
  
  private close(): void {
    if (!this.dialog) return;
    
    this.isOpen = false;
    
    // Schließe Dialog (restored focus automatisch)
    this.dialog.close();
    
    // Cleanup: Entferne Bilddaten
    const img = this.dialog.querySelector<HTMLImageElement>('.nwz-lightbox-image');
    const caption = this.dialog.querySelector<HTMLDivElement>('.nwz-lightbox-caption');
    
    if (img) {
      img.src = '';
      img.alt = '';
    }
    
    if (caption) {
      caption.textContent = '';
    }
    
    // Screen Reader Announcement
    this.announce('Lightbox geschlossen');
  }
  
  private loadImage(index: number): void {
    if (!this.dialog) return;
    
    const image = this.images[index];
    const img = this.dialog.querySelector<HTMLImageElement>('.nwz-lightbox-image');
    const caption = this.dialog.querySelector<HTMLDivElement>('.nwz-lightbox-caption');
    
    if (!img || !caption) return;
    
    // Setze Bild
    img.src = image.url;
    img.alt = image.alt;
    
    // Setze Caption mit Counter
    const counterText = `${index + 1} / ${this.images.length}`;
    const captionText = image.caption ? `${counterText} – ${image.caption}` : counterText;
    caption.textContent = captionText;
    
    // Update Navigation Buttons
    this.updateNavButtons();
    
    // Screen Reader Announcement
    setTimeout(() => {
      const srText = image.caption ? ': ' + image.caption : '';
      this.announce(`Bild ${index + 1} von ${this.images.length}${srText}`);
    }, 100);
  }
  
  private prev(): void {
    let newIndex = this.currentIndex - 1;
    
    if (newIndex < 0) {
      if (this.options.loop) {
        newIndex = this.images.length - 1;
      } else {
        return;
      }
    }
    
    this.currentIndex = newIndex;
    this.loadImage(newIndex);
  }
  
  private next(): void {
    let newIndex = this.currentIndex + 1;
    
    if (newIndex >= this.images.length) {
      if (this.options.loop) {
        newIndex = 0;
      } else {
        return;
      }
    }
    
    this.currentIndex = newIndex;
    this.loadImage(newIndex);
  }
  
  private updateNavButtons(): void {
    if (!this.dialog) return;
    
    const prevBtn = this.dialog.querySelector<HTMLButtonElement>('.nwz-lightbox-btn-prev');
    const nextBtn = this.dialog.querySelector<HTMLButtonElement>('.nwz-lightbox-btn-next');
    
    if (!prevBtn || !nextBtn) return;
    
    if (!this.options.loop) {
      prevBtn.disabled = this.currentIndex === 0;
      nextBtn.disabled = this.currentIndex === this.images.length - 1;
    } else {
      prevBtn.disabled = false;
      nextBtn.disabled = false;
    }
  }
  
  private trapFocus(e: KeyboardEvent): void {
    if (!this.content) return;
    
    const focusableElements = this.content.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    
    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  }
  
  private announce(message: string): void {
    if (!this.dialog) return;
    
    const announcer = this.dialog.querySelector<HTMLDivElement>('.nwz-lightbox-sr-only');
    if (!announcer) return;
    
    announcer.textContent = message;
  }
}

